'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

// ─── Globe Icon ───────────────────────────────────────────────────────────────
function GlobeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

// ─── Animated Section Wrapper ─────────────────────────────────────────────────
interface AnimatedSectionProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
}

function AnimatedSection({ children, delay = 0, className = '', style }: AnimatedSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(18px)',
        transition: `opacity 0.7s cubic-bezier(0.0,0,0.2,1) ${delay}ms, transform 0.7s cubic-bezier(0.0,0,0.2,1) ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Shared Transition Page Component ────────────────────────────────────────
export default function TransitionPage() {
  const [suitcaseVisible, setSuitcaseVisible] = useState(false);
  const [currentYear, setCurrentYear] = useState(2025);

  useEffect(() => {
    const timer = setTimeout(() => setSuitcaseVisible(true), 80);
    setCurrentYear(new Date().getFullYear());
    return () => clearTimeout(timer);
  }, []);

  const fontFamily = "'Plus Jakarta Sans', Inter, Manrope, sans-serif";

  const bodyTextStyle: React.CSSProperties = {
    fontFamily,
    fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
    fontWeight: 400,
    lineHeight: 1.8,
    color: '#4A5568',
    letterSpacing: '-0.005em',
    textAlign: 'left' as const,
    margin: 0,
  };

  return (
    <>
      {/* Skip to main content */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        onFocus={(e) => {
          e.currentTarget.style.position = 'fixed';
          e.currentTarget.style.left = '1rem';
          e.currentTarget.style.top = '1rem';
          e.currentTarget.style.width = 'auto';
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.overflow = 'visible';
          e.currentTarget.style.zIndex = '9999';
          e.currentTarget.style.padding = '0.5rem 1rem';
          e.currentTarget.style.backgroundColor = '#0F2D52';
          e.currentTarget.style.color = '#fff';
          e.currentTarget.style.borderRadius = '8px';
          e.currentTarget.style.fontSize = '0.875rem';
          e.currentTarget.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.currentTarget.style.position = 'absolute';
          e.currentTarget.style.left = '-9999px';
        }}
      >
        Skip to main content
      </a>

      <main
        id="main-content"
        role="main"
        style={{
          backgroundColor: '#FFFFFF',
          fontFamily,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* ── Hero Section ── */}
        <section
          aria-label="EduRise transition announcement: A new chapter begins"
          style={{
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: 'clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 4vw, 3rem)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            boxSizing: 'border-box',
          }}
        >
          <div className="hero-grid">

            {/* ── LEFT COLUMN: Suitcase ── */}
            <div className="hero-left">
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Subtle turquoise circular glow ~5% opacity */}
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    width: '420px',
                    height: '420px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(24,197,199,0.07) 0%, rgba(24,197,199,0.03) 50%, transparent 72%)',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />

                {/* Suitcase image */}
                <Image
                  src="/assets/images/Teams_Profile_Pictures__22_-1785800056541.jpg"
                  alt="Premium travel suitcase symbolising EduRise's new organisational chapter"
                  width={380}
                  height={430}
                  priority
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    width: 'clamp(260px, 30vw, 360px)',
                    height: 'auto',
                    display: 'block',
                    opacity: suitcaseVisible ? 1 : 0,
                    transform: suitcaseVisible ? 'translateY(0)' : 'translateY(18px)',
                    transition: 'opacity 0.9s cubic-bezier(0.0,0,0.2,1), transform 0.9s cubic-bezier(0.0,0,0.2,1)',
                  }}
                />
              </div>
            </div>

            {/* ── RIGHT COLUMN: Content ── */}
            <div className="hero-right">

              {/* Heading */}
              <AnimatedSection delay={150}>
                <h1
                  style={{
                    fontFamily,
                    fontSize: 'clamp(1.875rem, 3.5vw, 2.875rem)',
                    fontWeight: 800,
                    lineHeight: 1.1,
                    letterSpacing: '-0.03em',
                    color: '#0F2D52',
                    marginBottom: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                    textAlign: 'left',
                  }}
                >
                  A New Chapter <span style={{ color: '#18C5C7' }}>Begins</span>
                </h1>
              </AnimatedSection>

              {/* Paragraph 1 */}
              <AnimatedSection delay={280}>
                <p
                  style={{
                    ...bodyTextStyle,
                    marginBottom: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                  }}
                >
                  Since 2021, EduRise has been dedicated to advancing education,
                  developing leadership, and strengthening communities across
                  conflict-affected countries in the Middle East and North Africa.
                </p>
              </AnimatedSection>

              {/* Thin turquoise divider */}
              <AnimatedSection delay={380}>
                <div
                  role="separator"
                  aria-hidden="true"
                  style={{
                    width: '100%',
                    maxWidth: '340px',
                    height: '1.5px',
                    backgroundColor: '#18C5C7',
                    borderRadius: '999px',
                    opacity: 0.6,
                    marginBottom: 'clamp(1.25rem, 2.5vw, 1.75rem)',
                  }}
                />
              </AnimatedSection>

              {/* Paragraph 2 */}
              <AnimatedSection delay={480}>
                <p
                  style={{
                    ...bodyTextStyle,
                    marginBottom: 'clamp(1rem, 2vw, 1.375rem)',
                  }}
                >
                  Building on our work since 2021, we are now entering a new chapter.
                  Our work is now led from Wales, United Kingdom, where our primary
                  focus is supporting local communities across the UK while continuing
                  our commitment to underserved communities internationally through
                  education, partnerships, and community development.
                </p>
              </AnimatedSection>

              {/* Paragraph 3 */}
              <AnimatedSection delay={580}>
                <p
                  style={{
                    ...bodyTextStyle,
                    marginBottom: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                  }}
                >
                  Our new official website reflects this next stage of our journey.
                  We invite you to explore our renewed vision, discover our current
                  programmes, and see how we continue creating meaningful impact both
                  locally and internationally.
                </p>
              </AnimatedSection>

              {/* CTA Button */}
              <AnimatedSection delay={700}>
                <a
                  href="https://edurise.org.uk"
                  target="_self"
                  rel="noopener"
                  className="btn-cta"
                  aria-label="Visit the new EduRise official website at edurise.org.uk"
                >
                  <GlobeIcon />
                  <span>Visit Our New Website</span>
                </a>
              </AnimatedSection>

            </div>
            {/* ── end right column ── */}

          </div>
          {/* ── end hero-grid ── */}
        </section>

        {/* Footer */}
        <AnimatedSection delay={900} style={{ width: '100%' }}>
          <footer
            style={{
              paddingBottom: '2rem',
              textAlign: 'center',
            }}
          >
            <p
              style={{
                fontFamily,
                fontSize: '0.8125rem',
                fontWeight: 400,
                color: '#94A3B8',
                letterSpacing: '0.01em',
              }}
            >
              &copy; {currentYear} EduRise for Development. All Rights Reserved.
            </p>
          </footer>
        </AnimatedSection>

        {/* ── Scoped styles ── */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

          .hero-grid {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: clamp(2rem, 5vw, 3rem);
            width: 100%;
          }

          .hero-left {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .hero-right {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
          }

          @media (min-width: 1024px) {
            .hero-grid {
              flex-direction: row;
              align-items: center;
              gap: clamp(2.5rem, 4vw, 4rem);
            }

            .hero-left {
              flex: 0 0 40%;
              max-width: 40%;
            }

            .hero-right {
              flex: 0 0 60%;
              max-width: 60%;
            }
          }

          .btn-cta {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            background-color: #18C5C7;
            color: #ffffff;
            font-family: 'Plus Jakarta Sans', Inter, Manrope, sans-serif;
            font-size: 0.9375rem;
            font-weight: 600;
            letter-spacing: -0.01em;
            padding: 0.8125rem 1.875rem;
            border-radius: 14px;
            text-decoration: none;
            box-shadow: 0 4px 16px rgba(24, 197, 199, 0.28);
            transition: transform 0.22s cubic-bezier(0.0, 0, 0.2, 1),
                        box-shadow 0.22s cubic-bezier(0.0, 0, 0.2, 1),
                        background-color 0.22s ease;
            cursor: pointer;
            white-space: nowrap;
          }

          .btn-cta:hover,
          .btn-cta:focus-visible {
            background-color: #0FB8B4;
            transform: translateY(-3px);
            box-shadow: 0 8px 28px rgba(24, 197, 199, 0.42);
            outline: none;
          }

          .btn-cta:focus-visible {
            outline: 3px solid #0F2D52;
            outline-offset: 3px;
          }

          .btn-cta:active {
            transform: translateY(-1px);
          }
        `}</style>
      </main>
    </>
  );
}
